import React from "react";
import styles from "./StockReport.module.css";
import StockReportTable from "../components/StockReportTable";
import StockReportPDF from "./StockReportPDF";
import Button from "shared/Button";
import RadioButton from "shared/@forms/RadioButton";
import SuggestionInput from "shared/@forms/SuggestionInput";
import Loader from "shared/Loader";
import AlertNotice from "shared/AlertNotice";
import { stats as statsApi, errorMessage } from "shared/api";

const SORT_OPTIONS = [
  { value: "weeks_least", label: "Weeks Left: Least" },
  { value: "weeks_most", label: "Weeks Left: Most" },
  { value: "popular_least", label: "Popular: Least" },
  { value: "popular_most", label: "Popular: Most" },
  { value: "date_newest", label: "Date: Newest" },
  { value: "date_oldest", label: "Date: Oldest" },
  { value: "product_az", label: "Product: A to Z" },
  { value: "product_za", label: "Product: Z to A" },
];

const DEFAULT_SORT = "product_az";

const totalSold = (row) =>
  (row.salesByDate || []).reduce(
    (sum, entry) => sum + (Number(entry.quantity) || 0),
    0
  );

// Prefer the row's real creation date (added to the BFF stock report); fall
// back to trailing digits in the id, which only approximates insertion order
// for seeded `prd_NNNN` ids.
const addedOrder = (row) => {
  if (row.createdAt) {
    const t = new Date(row.createdAt).getTime();
    if (Number.isFinite(t)) return t;
  }
  const match = /(\d+)$/.exec(row.id || "");
  return match ? Number(match[1]) : 0;
};

const weeksValue = (row) => {
  const n = parseFloat(row?.available);
  return Number.isFinite(n) ? n : 0;
};

const SORT_COMPARATORS = {
  weeks_least: (a, b) => weeksValue(a) - weeksValue(b),
  weeks_most: (a, b) => weeksValue(b) - weeksValue(a),
  popular_least: (a, b) => totalSold(a) - totalSold(b),
  popular_most: (a, b) => totalSold(b) - totalSold(a),
  date_newest: (a, b) => addedOrder(b) - addedOrder(a),
  date_oldest: (a, b) => addedOrder(a) - addedOrder(b),
  product_az: (a, b) => (a.title || "").localeCompare(b.title || ""),
  product_za: (a, b) => (b.title || "").localeCompare(a.title || ""),
};

const csvEscape = (value) => {
  const str = String(value ?? "");
  return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
};

export function StockReport() {
  const [query, setQuery] = React.useState("");
  const [suggestions, setSuggestions] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [sortMenuOpen, setSortMenuOpen] = React.useState(false);
  const [sortBy, setSortBy] = React.useState(DEFAULT_SORT);
  const reqIdRef = React.useRef(0);

  React.useEffect(() => {
    const reqId = ++reqIdRef.current;
    setLoading(true);
    setError(null);
    statsApi
      .stockReport()
      .then((res) => {
        if (reqId !== reqIdRef.current) return;
        setRows(Array.isArray(res) ? res : []);
        setLoading(false);
      })
      .catch((err) => {
        if (reqId !== reqIdRef.current) return;
        setError(errorMessage(err, "Couldn't load the stock report."));
        setLoading(false);
      });
  }, []);

  const filteredData = React.useMemo(() => {
    const trimmedQuery = query.trim().toLowerCase();
    const matches = !trimmedQuery
      ? rows
      : rows.filter(({ sku, bin, title }) => {
          const haystack = `${sku} ${bin} ${title}`.toLowerCase();
          return haystack.includes(trimmedQuery);
        });

    const comparator =
      SORT_COMPARATORS[sortBy] || SORT_COMPARATORS[DEFAULT_SORT];
    return [...matches].sort(comparator);
  }, [query, rows, sortBy]);

  const fetchSuggestions = React.useCallback(
    (value) => {
      const normalized = value.trim().toLowerCase();

      if (!normalized) {
        setSuggestions([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      const matches = rows.filter(({ sku, bin, title }) => {
        const haystack = `${sku} ${bin} ${title}`.toLowerCase();
        return haystack.includes(normalized);
      });

      setSuggestions(matches);
      setIsLoading(false);
    },
    [rows]
  );

  const handleQueryChange = React.useCallback((event) => {
    setQuery(event.target.value);
  }, []);

  const handleExport = React.useCallback(() => {
    if (!filteredData.length) return;

    const dateColumns = filteredData[0].salesByDate || [];
    const header = [
      "SKU",
      "Bin",
      "Title",
      ...dateColumns.map((entry) => entry.date),
      "6 W/Avg",
      "On Hand",
      "W/Available",
    ];

    const lines = filteredData.map((row) => {
      const sales = (row.salesByDate || []).map((entry) => entry.quantity);
      return [
        row.sku,
        row.bin,
        row.title,
        ...sales,
        row.average,
        row.onHand,
        row.available,
      ]
        .map(csvEscape)
        .join(",");
    });

    const csv = [header.map(csvEscape).join(","), ...lines].join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "stock-report.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [filteredData]);

  const renderSuggestion = React.useCallback(
    (item, closePanel, index) => (
      <button
        key={`${item.sku}-${item.bin}-${index}`}
        type="button"
        className={styles.suggestionRow}
        onClick={() => {
          setQuery(item.title);
          closePanel();
        }}
        title={`Use "${item.title}" as the search term.`}
      >
        <span className={styles.suggestionLabel}>Item:</span>
        <span className={styles.suggestionPill}>
          {item.title} (SKU: {item.sku})
        </span>
      </button>
    ),
    []
  );

  return (
    <div className={styles.stockReport}>
      <div className={styles.toolbar}>
        <div className={styles.searchInput}>
          <SuggestionInput
            type="search"
            placeholder="Search items"
            aria-label="Search stock report rows"
            value={query}
            onChange={handleQueryChange}
            fetchSuggestions={fetchSuggestions}
            suggestions={suggestions}
            isLoading={isLoading}
            minLength={1}
          >
            {renderSuggestion}
          </SuggestionInput>
        </div>
      </div>

      <section className={styles.section}>
        <header className={styles.sectionHeader}>
          <div className={styles.sectionTitleGroup}>
            <h2 className={styles.sectionTitle}>Stock Report</h2>
          </div>

          <div className={styles.sectionActions}>
            <Button
              type="button"
              className={`${styles.actionButton} ${styles.export}`}
              onClick={handleExport}
              disabled={!filteredData.length}
              title="Download this report as a CSV file."
            >
              CSV
            </Button>
            <StockReportPDF data={filteredData}>
              <Button
                type="button"
                className={`${styles.actionButton} ${styles.export}`}
                disabled={!filteredData.length}
                title="Download this report as a PDF file."
              >
                PDF
              </Button>
            </StockReportPDF>
            <Button
              type="button"
              className={`${styles.actionButton} ${styles.sort}`}
              onClick={() => setSortMenuOpen(true)}
              title="Choose how to sort the report."
            >
              Sort
            </Button>
          </div>
        </header>

        {error ? (
          <AlertNotice title="Couldn't load stock report">{error}</AlertNotice>
        ) : loading ? (
          <Loader size={60} />
        ) : (
          <StockReportTable stockItemData={filteredData} allRows={rows} />
        )}
      </section>

      {sortMenuOpen && (
        <div
          className={styles.sortModalBackdrop}
          onClick={() => setSortMenuOpen(false)}
        >
          <div
            className={styles.sortModal}
            onClick={(event) => event.stopPropagation()}
          >
            <div className={styles.sortModalHeader}>Sort By</div>
            <div className={styles.sortModalContent}>
              {SORT_OPTIONS.map((opt) => (
                <label className={styles.sortOption} key={opt.value}>
                  <RadioButton
                    name="stock-sort"
                    value={opt.value}
                    checked={sortBy === opt.value}
                    onChange={() => setSortBy(opt.value)}
                  />
                  <span>{opt.label}</span>
                </label>
              ))}
            </div>
            <button
              type="button"
              className={styles.sortModalClose}
              onClick={() => setSortMenuOpen(false)}
              title="Close this dialog without saving."
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
